import React, { useCallback, useEffect, useRef, useState } from 'react';
import FlyOutMenu from '../../../../core/components/FlyOutMenu';
import { MonitorHistoryItem, MonitorConfig } from '../../data/datamonitorRepo';
import datamonitorRepo from '../../data/datamonitorRepo';
import MonitorStatusIcon from './MonitorStatusIcon';

type MonitorHistoryFlyOutProps = {
    monitorId: string | null;
    title: string;
    monitorType: string;
    onClose: () => void;
    ignoreClickRef?: React.RefObject<HTMLElement>;
};

const MonitorHistoryFlyOut = ({
    monitorId,
    title,
    monitorType,
    onClose,
    ignoreClickRef,
}: MonitorHistoryFlyOutProps): JSX.Element => {
    const [history, setHistory] = useState<MonitorHistoryItem[]>([]);
    const [config, setConfig] = useState<MonitorConfig | null>(null);
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!monitorId) {
            setHistory([]);
            setConfig(null);
            setLoading(false);
            return;
        }

        // Increment request ID to invalidate any in-flight request
        const currentRequestId = ++requestIdRef.current;
        setLoading(true);
        setHistory([]);
        setConfig(null);

        const load = async (): Promise<void> => {
            try {
                const items = await datamonitorRepo.getHistory(monitorId);
                if (currentRequestId !== requestIdRef.current) return; // cancelled
                setHistory(items);
            } catch {
                if (currentRequestId !== requestIdRef.current) return;
                setHistory([]);
            }
            if (currentRequestId !== requestIdRef.current) return;
            setLoading(false);

            // Load config after history resolves
            try {
                const cfg = await datamonitorRepo.getConfig(monitorId, true);
                if (currentRequestId !== requestIdRef.current) return;
                setConfig(cfg);
            } catch {
                // config is optional
            }
        };
        load();

        return (): void => {
            // Cancel on unmount or monitorId change
            // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: incrementing ref to invalidate stale requests
            requestIdRef.current++;
        };
    }, [monitorId]);

    const handleClose = useCallback((): void => {
        // Cancel any in-flight requests
        requestIdRef.current++;
        setLoading(false);
        onClose();
    }, [onClose]);

    const formatDate = (dateStr: string): string => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            });
        } catch {
            return dateStr;
        }
    };

    const showLookback = !!(
        config?.Lookback &&
        (monitorType === 'AFFILIATION.ACKNOWLEDGEMENT.RECEIVED' || monitorType === 'AFFILIATION.INVOICES.RECEIVED')
    );

    return (
        <FlyOutMenu
            title={title}
            isOpen={!!monitorId}
            onClose={handleClose}
            width={600}
            overflowY="auto"
            ignoreClickRef={ignoreClickRef}
        >
            {loading ? (
                <div className="monitor-history-loading">
                    <div className="shimmer-line shimmer-line--wide" />
                    <div className="shimmer-line shimmer-line--medium" />
                    <div className="shimmer-table">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div
                                key={i}
                                className="shimmer-row"
                            >
                                <div className="shimmer-cell shimmer-cell--status" />
                                <div className="shimmer-cell shimmer-cell--text" />
                                <div className="shimmer-cell shimmer-cell--date" />
                                <div className="shimmer-cell shimmer-cell--date" />
                                <div className="shimmer-cell shimmer-cell--detail" />
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="monitor-history-content">
                    <section className="monitor-info-section">
                        <h3>Information</h3>
                        <div className="info-row">
                            <div className="info-group">
                                <label>Monitor Type:</label>
                                <span>{monitorType}</span>
                            </div>
                        </div>
                        {config ? (
                            <div className="info-row">
                                <div className="info-group">
                                    <label>Monitor Rule:</label>
                                    <span>{config.Active ? 'ON' : 'OFF'}</span>
                                </div>
                                <div className="info-group">
                                    <label>Alerts:</label>
                                    <span>{config.Alert ? 'ON' : 'OFF'}</span>
                                </div>
                                <div className="info-group">
                                    <label>Fails After:</label>
                                    <span>
                                        {config.Interval} {config.IntervalType}s
                                    </span>
                                </div>
                                {showLookback ? (
                                    <div className="info-group">
                                        <label>Lookback:</label>
                                        <span>
                                            {config.Lookback} {config.LookbackType}s
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </section>
                    <section className="monitor-history-section">
                        <h3>History</h3>
                        {history.length === 0 ? (
                            <p className="no-records">No Records Found</p>
                        ) : (
                            <>
                                <p className="record-count">Viewing {history.length} records</p>
                                <table className="table-simple">
                                    <thead>
                                        <tr>
                                            <td style={{ width: 60 }}>Status</td>
                                            <td style={{ width: '25%' }}>Elapsed</td>
                                            <td style={{ width: '20%' }}>Changed</td>
                                            <td style={{ width: '20%' }}>Last Activity</td>
                                            <td style={{ width: '25%' }}>Detail</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <MonitorStatusIcon status={item.Status} />
                                                </td>
                                                <td>{item.ElapsedTxt}</td>
                                                <td>{formatDate(item.Changed)}</td>
                                                <td>{formatDate(item.LastChecked)}</td>
                                                <td className="detail-cell">{item.Details}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </section>
                </div>
            )}
        </FlyOutMenu>
    );
};

export default MonitorHistoryFlyOut;
